import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

export type CategoriaServicio = {
  id_categoria_servicio: string;
  id_empresa: string;
  nombre: string;
  imagen: string | null;
  icono: string | null;
  orden: number | null;
  activo: boolean;
};

export function useCategoriasServicio(idEmpresa: string) {
  const [categorias, setCategorias] = useState<CategoriaServicio[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const idUsuario =
    typeof window !== 'undefined' ? localStorage.getItem('id_usuario') : null;

  const BUCKET = 'bookoll';

  // ============================================================
  // COMPRESIÓN IMAGEN
  // ============================================================
  const comprimirImagen = (file: File): Promise<File> =>
    new Promise((resolve, reject) => {
      const MAX_PX = 800;
      const QUALITY = 0.82;

      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        let { width, height } = img;

        if (width > MAX_PX || height > MAX_PX) {
          if (width >= height) {
            height = Math.round((height * MAX_PX) / width);
            width = MAX_PX;
          } else {
            width = Math.round((width * MAX_PX) / height);
            height = MAX_PX;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('canvas toBlob failed'));
              return;
            }

            resolve(
              new File([blob], 'categoria.jpg', {
                type: 'image/jpeg',
              })
            );
          },
          'image/jpeg',
          QUALITY
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Image load error'));
      };

      img.src = objectUrl;
    });

  // ============================================================
  // FETCH
  // ============================================================
  const fetchCategorias = useCallback(async () => {
    if (!idEmpresa) return;

    setLoading(true);
    setError(null);

    const { data, error } = await supabase
      .from('categoria_servicio')
      .select('*')
      .eq('id_empresa', idEmpresa)
      .order('orden', { ascending: true })
      .order('nombre', { ascending: true });

    if (error) {
      Sentry.captureException(error, { extra: { idEmpresa } }); // 👈
      setError('Error al cargar categorías');
      toast.error('Error al cargar categorías');
      setCategorias([]);
    } else {
      setCategorias((data as CategoriaServicio[]) || []);
    }

    setLoading(false);
  }, [idEmpresa]);

  // ============================================================
  // STORAGE HELPERS
  // ============================================================
  const subirImagen = async (
    file: File,
    categoriaId: string
  ): Promise<string | null> => {
    try {
      const comprimido = await comprimirImagen(file);

      const path = `categoria_empresa/${idEmpresa}/${categoriaId}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(path, comprimido, {
          upsert: true,
          cacheControl: '0',
          contentType: 'image/jpeg',
        });

      if (uploadError) throw uploadError;

      const publicUrl = supabase.storage.from(BUCKET).getPublicUrl(path)
        .data.publicUrl;

      // La ruta es siempre la misma: la versión en la URL evita que el
      // navegador y el CDN sigan sirviendo la imagen anterior.
      return `${publicUrl}?v=${Date.now()}`;
    } catch (err) {
      Sentry.captureException(err, { extra: { idEmpresa, categoriaId } }); // 👈
      console.error(err);
      toast.error('Error al subir imagen');
      return null;
    }
  };

  const eliminarImagen = async (categoriaId: string) => {
    const path = `categoria_empresa/${idEmpresa}/${categoriaId}.jpg`;
    const { error } = await supabase.storage.from(BUCKET).remove([path]);

    if (error) {
      Sentry.captureException(error, { extra: { idEmpresa, categoriaId } }); // 👈
    }
  };

  // ============================================================
  // CREATE
  // ============================================================
  const createCategoria = async (nombre: string, file?: File) => {
    setLoading(true);

    const { data, error } = await supabase
      .from('categoria_servicio')
      .insert([
        {
          id_empresa: idEmpresa,
          nombre,
          activo: true,
          usuario_creacion: idUsuario,
        },
      ])
      .select('*')
      .single();

    if (error || !data) {
      Sentry.captureException(error, { extra: { idEmpresa, nombre } }); // 👈
      toast.error('Error al crear categoría');
      setLoading(false);
      return { ok: false };
    }

    let categoria = data as CategoriaServicio;

    if (file) {
      const url = await subirImagen(file, categoria.id_categoria_servicio);

      if (url) {
        const { data: updated, error: updateError } = await supabase
          .from('categoria_servicio')
          .update({
            imagen: url,
            usuario_edicion: idUsuario,
          })
          .eq('id_categoria_servicio', categoria.id_categoria_servicio)
          .select('*')
          .single();

        if (updateError) {
          Sentry.captureException(updateError, {
            extra: { idEmpresa, categoriaId: categoria.id_categoria_servicio },
          }); // 👈
        }

        if (updated) categoria = updated;
      }
    }

    setCategorias((prev) => [...prev, categoria]);
    setLoading(false);

    return { ok: true, data: categoria };
  };

  // ============================================================
  // UPDATE
  // ============================================================
  const updateCategoria = async (
    id: string,
    nombre: string,
    file?: File | null
  ) => {
    setLoading(true);

    const { data, error } = await supabase
      .from('categoria_servicio')
      .update({
        nombre,
        usuario_edicion: idUsuario,
      })
      .eq('id_categoria_servicio', id)
      .select('*')
      .single();

    if (error || !data) {
      Sentry.captureException(error, { extra: { idEmpresa, categoriaId: id } }); // 👈
      toast.error('Error al actualizar categoría');
      setLoading(false);
      return { ok: false };
    }

    let categoria = data as CategoriaServicio;

    if (file === null) {
      await eliminarImagen(id);

      const { data: updated, error: updateError } = await supabase
        .from('categoria_servicio')
        .update({
          imagen: null,
          usuario_edicion: idUsuario,
        })
        .eq('id_categoria_servicio', id)
        .select('*')
        .single();

      if (updateError) {
        Sentry.captureException(updateError, {
          extra: { idEmpresa, categoriaId: id },
        }); // 👈
      }

      if (updated) categoria = updated;
    }

    if (file instanceof File) {
      await eliminarImagen(id);
      const url = await subirImagen(file, id);

      if (url) {
        const { data: updated, error: updateError } = await supabase
          .from('categoria_servicio')
          .update({
            imagen: url,
            usuario_edicion: idUsuario,
          })
          .eq('id_categoria_servicio', id)
          .select('*')
          .single();

        if (updateError) {
          Sentry.captureException(updateError, {
            extra: { idEmpresa, categoriaId: id },
          }); // 👈
        }

        if (updated) categoria = updated;
      }
    }

    setCategorias((prev) =>
      prev.map((c) => (c.id_categoria_servicio === id ? categoria : c))
    );

    setLoading(false);
    return { ok: true, data: categoria };
  };

  // ============================================================
  // DELETE
  // ============================================================
  const deleteCategoria = async (id: string) => {
    setLoading(true);

    await eliminarImagen(id);

    const { error } = await supabase
      .from('categoria_servicio')
      .delete()
      .eq('id_categoria_servicio', id);

    if (error) {
      Sentry.captureException(error, { extra: { idEmpresa, categoriaId: id } }); // 👈
      toast.error('Error al eliminar categoría');
      setLoading(false);
      return { ok: false };
    }

    setCategorias((prev) => prev.filter((c) => c.id_categoria_servicio !== id));

    setLoading(false);
    return { ok: true };
  };

  return {
    categorias,
    loading,
    error,
    fetchCategorias,
    createCategoria,
    updateCategoria,
    deleteCategoria,
  };
}
